import React, { useEffect, useState } from 'react';
import { X, Trophy, Star, Zap, Crown } from 'lucide-react';

const AchievementNotification = ({ achievement, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        // Apparition
        setTimeout(() => setIsVisible(true), 100);

        // Disparition automatique après 5 secondes
        const timer = setTimeout(() => {
            handleClose();
        }, 5000);

        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsLeaving(true);
        setTimeout(() => {
            setIsVisible(false);
            onClose();
        }, 300);
    };

    // Couleurs selon la rareté
    const rarityStyles = {
        common: {
            bg: 'from-gray-700 to-gray-800',
            border: 'border-gray-500',
            glow: 'shadow-gray-500/50',
            icon: Star,
            color: 'text-gray-300'
        },
        rare: {
            bg: 'from-blue-600 to-blue-700',
            border: 'border-blue-400',
            glow: 'shadow-blue-500/50',
            icon: Zap,
            color: 'text-blue-300'
        },
        epic: {
            bg: 'from-purple-600 to-purple-700',
            border: 'border-purple-400',
            glow: 'shadow-purple-500/50',
            icon: Trophy,
            color: 'text-purple-300'
        },
        legendary: {
            bg: 'from-yellow-500 to-orange-600',
            border: 'border-yellow-400',
            glow: 'shadow-yellow-500/50',
            icon: Crown,
            color: 'text-yellow-300'
        }
    };

    const style = rarityStyles[achievement.rarity] || rarityStyles.common;
    const RarityIcon = style.icon;

    return (
        <div
            className={`fixed top-20 right-4 z-50 transition-all duration-300 ${
                isVisible && !isLeaving
                    ? 'translate-x-0 opacity-100'
                    : 'translate-x-full opacity-0'
            }`}
        >
            <div
                className={`bg-gradient-to-r ${style.bg} border-2 ${style.border} rounded-xl p-4 w-96 shadow-2xl ${style.glow} backdrop-blur-sm`}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <RarityIcon className={`w-5 h-5 ${style.color}`} />
                        <span className={`text-sm font-bold ${style.color} uppercase tracking-wider`}>
                            Achievement Débloqué !
                        </span>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-white/60 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="text-5xl flex-shrink-0">
                        {achievement.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                        <h3 className="text-white font-bold text-lg mb-1">
                            {achievement.name}
                        </h3>
                        <p className="text-white/80 text-sm mb-2">
                            {achievement.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs">
                            <span className={`${style.color} font-bold`}>
                                {achievement.points} pts
                            </span>
                            <span className="text-white/60">
                                {achievement.rarity.charAt(0).toUpperCase() + achievement.rarity.slice(1)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Progress bar animation */}
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-white transition-all duration-1000 ease-out"
                        style={{
                            width: isVisible ? '100%' : '0%',
                            transition: 'width 5s linear'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default AchievementNotification;